import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./ProjectDetail.css";

export const RealtorProject = () => {
  const [activeDevice, setActiveDevice] = useState("desktop");

  const projectData = {
    title: "Realtor Parminder Singh",
    subtitle: "Real Estate Website",
    url: "https://realtorparmindersingh.ca",
    client: "Parminder Singh",
    industry: "Real Estate",
    duration: "4 weeks",
    year: "2024",
    description: "A modern, professional real estate website designed to help Parminder Singh showcase property listings and connect with potential home buyers in Winnipeg and surrounding areas.",
    challenge: "Parminder needed a professional online presence to compete in the competitive Winnipeg real estate market. The website needed to be fast, mobile-friendly, and easy for clients to navigate while showcasing property listings effectively.",
    solution: "We built a fully responsive website with a clean, modern design that works seamlessly across all devices. The site features easy-to-use property search, beautiful property galleries, and clear call-to-action buttons to encourage client inquiries.",
    results: [
      { metric: "50%", label: "Increase in Inquiries" },
      { metric: "3x", label: "More Website Traffic" },
      { metric: "100%", label: "Mobile Responsive" },
      { metric: "Fast", label: "Page Load Speed" },
    ],
  };

  const features = [
    {
      icon: "📱",
      title: "Fully Responsive Design",
      description: "Optimized for desktop, tablet, and mobile devices ensuring a seamless experience for all users."
    },
    {
      icon: "🏠",
      title: "Property Listings",
      description: "Beautiful property showcase with high-quality images, detailed descriptions, and easy navigation."
    },
    {
      icon: "🔍",
      title: "Property Search",
      description: "Intuitive search functionality allowing visitors to find properties based on their preferences."
    },
    {
      icon: "📞",
      title: "Lead Generation",
      description: "Strategic call-to-action buttons and contact forms to maximize client inquiries."
    },
    {
      icon: "⚡",
      title: "Fast Performance",
      description: "Optimized for speed with fast loading times and smooth interactions."
    },
    {
      icon: "🎨",
      title: "Modern UI/UX",
      description: "Clean, professional design that builds trust and engages visitors."
    },
    {
      icon: "📊",
      title: "SEO Optimized",
      description: "Built with SEO best practices to rank well in search engines for local real estate searches."
    },
    {
      icon: "🔒",
      title: "Secure & Reliable",
      description: "Hosted on secure servers with SSL encryption for visitor safety."
    },
  ];

  const techStack = [
    { name: "React", icon: "⚛️" },
    { name: "CSS3", icon: "🎨" },
    { name: "JavaScript", icon: "📜" },
    { name: "Firebase", icon: "🔥" },
    { name: "Google Maps", icon: "🗺️" },
    { name: "Responsive Design", icon: "📱" },
  ];

  return (
    <div className="project-detail-page">
      {/* Back Link - positioned below nav header */}
      <div className="back-link-container">
        <Link to="/projects" className="back-link">← Back to Projects</Link>
      </div>

      {/* Navigation */}
      <nav className="project-nav">
        <a href={projectData.url} target="_blank" rel="noopener noreferrer" className="visit-site">
          Visit Live Site ↗
        </a>
      </nav>

      {/* Hero Section */}
      <header className="project-hero">
        <div className="container">
          <div className="hero-content">
            <span className="project-badge">{projectData.industry}</span>
            <h1>{projectData.title}</h1>
            <p className="hero-subtitle">{projectData.subtitle}</p>
            <p className="hero-description">{projectData.description}</p>
            <div className="project-meta">
              <div className="meta-item">
                <span className="meta-label">Client</span>
                <span className="meta-value">{projectData.client}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Industry</span>
                <span className="meta-value">{projectData.industry}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Duration</span>
                <span className="meta-value">{projectData.duration}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Year</span>
                <span className="meta-value">{projectData.year}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Device Showcase */}
      <section className="device-showcase">
        <div className="container">
          <h2>Responsive Across All Devices</h2>
          <p className="section-subtitle">See how the website looks on different screen sizes</p>
          
          {/* Device Tabs */}
          <div className="device-tabs">
            <button 
              className={activeDevice === "desktop" ? "active" : ""} 
              onClick={() => setActiveDevice("desktop")}
            >
              🖥️ Desktop
            </button>
            <button 
              className={activeDevice === "tablet" ? "active" : ""} 
              onClick={() => setActiveDevice("tablet")}
            >
              📱 Tablet
            </button>
            <button 
              className={activeDevice === "mobile" ? "active" : ""} 
              onClick={() => setActiveDevice("mobile")}
            >
              📲 Mobile
            </button>
          </div>

          {/* Device Mockups */}
          <div className="device-mockups">
            {/* Desktop View */}
            <div className={`device-mockup desktop ${activeDevice === "desktop" ? "active" : ""}`}>
              <div className="mockup-frame">
                <div className="mockup-header">
                  <div className="mockup-dots">
                    <span className="dot red"></span>
                    <span className="dot yellow"></span>
                    <span className="dot green"></span>
                  </div>
                  <div className="mockup-url">realtorparmindersingh.ca</div>
                </div>
                <div className="mockup-screen">
                  <img 
                    src="/img/projects/realtor-desktop.png" 
                    alt="Realtor Parminder Singh - Desktop View" 
                    className="screenshot-image"
                  />
                </div>
              </div>
            </div>

            {/* Tablet View */}
            <div className={`device-mockup tablet ${activeDevice === "tablet" ? "active" : ""}`}>
              <div className="mockup-frame tablet-frame">
                <div className="mockup-screen">
                  <img 
                    src="/img/projects/realtor-tablet.png" 
                    alt="Realtor Parminder Singh - Tablet View" 
                    className="screenshot-image"
                  />
                </div>
              </div>
            </div>

            {/* Mobile View */}
            <div className={`device-mockup mobile ${activeDevice === "mobile" ? "active" : ""}`}>
              <div className="mockup-frame mobile-frame">
                <div className="mockup-notch"></div>
                <div className="mockup-screen">
                  <img 
                    src="/img/projects/realtor-mobile.png" 
                    alt="Realtor Parminder Singh - Mobile View" 
                    className="screenshot-image"
                  />
                </div>
                <div className="mockup-home-indicator"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="challenge-solution">
        <div className="container">
          <div className="cs-grid">
            <div className="cs-card challenge">
              <span className="cs-icon">🎯</span>
              <h3>The Challenge</h3>
              <p>{projectData.challenge}</p>
            </div>
            <div className="cs-card solution">
              <span className="cs-icon">💡</span>
              <h3>Our Solution</h3>
              <p>{projectData.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="results-section">
        <div className="container">
          <h2>Project Results</h2>
          <div className="results-grid">
            {projectData.results.map((result, idx) => (
              <div key={idx} className="result-card">
                <span className="result-metric">{result.metric}</span>
                <span className="result-label">{result.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="container">
          <h2>What We Built</h2>
          <p className="section-subtitle">Key features and capabilities delivered in this project</p>
          <div className="features-grid">
            {features.map((feature, idx) => (
              <div key={idx} className="feature-card">
                <span className="feature-icon">{feature.icon}</span>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="tech-section">
        <div className="container">
          <h2>Technology Stack</h2>
          <div className="tech-grid">
            {techStack.map((tech, idx) => (
              <div key={idx} className="tech-card">
                <span className="tech-icon">{tech.icon}</span>
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="project-cta">
        <div className="container">
          <h2>Ready to Build Your Project?</h2>
          <p>Let's create something amazing for your business</p>
          <div className="cta-buttons">
            <a href={projectData.url} target="_blank" rel="noopener noreferrer" className="btn-primary">
              Visit Live Site ↗
            </a>
            <a href="/#contact" className="btn-secondary">
              Start Your Project
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="project-footer">
        <div className="container">
          <p>© 2025 9Reason • Software Development Agency</p>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/projects">Projects</Link>
            <Link to="/community">Community</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

